<script lang="ts">
  // The "Group" pill, made clickable: shows every rider from the group ride (matching the
  // avatars shown alongside it), the name of their ride, and — if their ride was a
  // breakthrough — a compact trophy row with the TP/HIE/PP signature change.
  import { cn } from "@/lib/utils";
  import { tagVariants, type Tag } from "./ActivityTag";
  import * as Popover from "$lib/components/ui/popover/index.js";
  import type { GroupMember } from "./ActivityFunctions";
  import { getSquadmateColor, trophyColor, tidy, signatureDeltas } from "./GroupMemberAvatars.svelte";

  export let tag: Tag;
  export let members: GroupMember[] = [];

  const MAX_RIDE_NAME = 28;
  function truncate(name: string): string {
    if (!name) return "";
    return name.length > MAX_RIDE_NAME ? name.slice(0, MAX_RIDE_NAME - 1).trimEnd() + "…" : name;
  }
</script>

<Popover.Root portal="#shadcn-ui-dialog-target">
  <Popover.Trigger asChild let:builder>
    <button
      use:builder.action
      {...builder}
      type="button"
      class={cn(tagVariants({ variant: tag.variant, size: tag.size }), "cursor-pointer border-0 transition hover:brightness-90 focus:outline-none")}
    >
      {#if tag.faIcons}
        {#each { length: tag.faIcons.length } as _, i (tag.faIcons[i])}
          <i class={cn(tag.faIcons[i], tag.faIconTypes?.[i], tag.label ? "mr-1" : "")} />
        {/each}
      {/if}
      {tag.label}
    </button>
  </Popover.Trigger>
  <Popover.Content class="w-80 max-h-96 overflow-y-auto space-y-2.5">
    <div class="font-semibold text-sm">Group Ride</div>
    {#each members as member (member.path)}
      <div class="flex items-start gap-2">
        <span
          class="mt-1.5 h-2.5 w-2.5 rounded-full shrink-0"
          style={`background-color:${getSquadmateColor(member.username)}`}
        />
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-1.5 min-w-0">
            <span class="font-medium text-sm shrink-0">{member.display_name}</span>
            <a
              class="truncate text-sm text-xerteal-600 hover:underline"
              href={member.activity_url ?? `/activities/${member.path}?group_sharing=Group`}
              target="activity"
              rel="noopener noreferrer"
              title={member.name}
            >
              {truncate(member.name)}
            </a>
          </div>
          {#if member.breakthrough}
            <div class="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
              <i class={cn("fa-regular fa-trophy", trophyColor(member.breakthrough.medal))} />
              {#each signatureDeltas(member.breakthrough) as d (d.label)}
                <span>
                  {d.label} <b class="text-foreground">{d.value}</b>
                  <span class={d.delta >= 0 ? "text-[#5CA907]" : "text-[#FF2222]"}
                    >({d.delta >= 0 ? "+" : "-"}{tidy(d.delta)}{d.unit})</span
                  >
                </span>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </Popover.Content>
</Popover.Root>
